<script setup lang="ts">
import { RouterView } from 'vue-router'
import { computed, type ComputedRef, onMounted, type Ref, ref, watch } from 'vue'

// neat checkbox stuff!
// https://hamed-ehtesham.github.io/pretty-checkbox-vue/
import logo from '@/assets/logo.svg'
import { useUserStore } from '@/stores/user'
import { useGlobalStore } from '@/stores/global'
import { useConfigStore } from '@/stores/config'
import { useTheme } from 'vuetify'
import { routerPush } from '@/router'
import { storeToRefs } from 'pinia'

import { CONFIG } from '@/config'
import { accountLogout, navigateToLogin } from '@/helpers/account-helpers.ts'

import ConfirmDialog from '@/components/helpers/ConfirmDialog.vue'
import SecondSidebar from '@/components/SecondSidebar.vue'
import LoginForm from '@/components/user/LoginForm.vue'

/** Title */
const title = CONFIG.APP_TITLE

// @tcropley -- reference this for buildout, particularly meta information and how to use it
// https://github.com/vuetifyjs/vuetify/blob/master/packages/docs/src/App.vue

/** Vuetify Theme */
const theme = useTheme()

/** Global Store */
const globalStore = useGlobalStore()

/** Config Store */
const configStore = useConfigStore()
const currentGlobalStore = storeToRefs(globalStore)
const sbItems = ref(currentGlobalStore.sbQueueMessages)

/** User / Participant / Account Store **/
const userStore = useUserStore()
const currentUser = storeToRefs(userStore)
const isLoggedIn = ref(currentUser.isLoggedin)

const drawer = ref<boolean>(false)

function logoutApplicationReset() {
  accountLogout()
  // routerPush('home')
}

/** loading overlay visibility */
const loading = computed({
  get: () => globalStore.loadingCounter > 0,
  set: (v) => globalStore.setLoading(v)
})

/** Snackbar visibility */
const snackbarVisibility: Ref<boolean> = ref(false)

const showLoginFormDialog = ref(false)

function checkUrlHash() {
  // Open modal if the hash matches a specific value
  showLoginFormDialog.value = window.location.hash === '#login'
}

function closeLoginFormDialog() {
  showLoginFormDialog.value = false
  window.location.hash = ''
  // window.history.pushState(null, '', ' '); // Removes the hash
}

/** Toggle Dark mode */
const isDark: ComputedRef<string> = computed(() => (configStore.theme ? 'dark' : 'light'))

// When snackbar text has been set, show snackbar.
watch(
  () => globalStore.message,
  (message) => (snackbarVisibility.value = message !== '')
)

function ShowMessageOnClick() {
  globalStore.setMessage('THIS IS THE MESSAGE !!')
  routerPush('about')
}

onMounted(() => {
  document.title = title
  // console.log([isDark.value])

  window.addEventListener('hashchange', checkUrlHash)

  if (window.location.hash === '#login') {
    showLoginFormDialog.value = true
  }
})

// async function logoutApplicationReset() {
//   await accountLogout()
//   // userStore.logout()
//   // routerPush('home')
// }
</script>

<template>
  <v-layout :theme="isDark" class="rounded rounded-md">
    <v-app-bar :elevation="2">
      <template #prepend>
        <v-app-bar-nav-icon @click="drawer = !drawer" />
      </template>

      <v-avatar :tile="true" :image="logo" alt="logo" />

      <v-app-bar-title class="text-primary font-weight-bold" :to="{ name: 'home' }"
        >{{ CONFIG.App }}
      </v-app-bar-title>

      <template #append>
        <v-btn icon="mdi-heart"></v-btn>
        <v-btn icon="mdi-magnify" @click="ShowMessageOnClick"></v-btn>
        <v-btn icon="mdi-dots-vertical"></v-btn>
        <!-- Toggle Dark mode -->
        <v-btn icon="mdi-theme-light-dark" variant="flat" @click="configStore.toggleTheme" />

        <v-btn-group v-if="isLoggedIn">
          <v-btn icon="mdi-logout" class="text-red" @click="logoutApplicationReset"></v-btn>
        </v-btn-group>

        <v-btn v-if="!isLoggedIn" icon="mdi-account" @click="navigateToLogin"></v-btn>
      </template>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" :width="300">
      <SecondSidebar />
      <!--      <SidebarContent></SidebarContent>-->
    </v-navigation-drawer>

    <v-main
      class="d-flex align-center justify-center"
      style="min-height: 300px; background-color: #cdcdcd; border: 1px solid #900"
    >
      <component :is="$route.meta.layout || 'div'">
        <!--        <router-view v-slot="{ Component, route }">-->
        <router-view>
          <!--          <transition name="fade">-->
          <!--            &lt;!&ndash;            <component :is="Component" :key="route.name" />&ndash;&gt;-->
          <!--          </transition>-->
        </router-view>
      </component>
    </v-main>

    <v-overlay v-model="loading" :persistent="true" app class="justify-center align-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <v-snackbar-queue v-model:modelValue="sbItems">
      <template v-slot:actions>
        <v-btn icon="mdi-close-circle"></v-btn>
      </template>
    </v-snackbar-queue>

    <confirm-dialog />

    <!--    <v-snackbar v-model="snackbarVisibility" @update:model-value="onSnackbarChanged" location="top">-->
    <!--      {{ snackbarText }}-->
    <!--      <template #actions>-->
    <!--        <v-btn icon="mdi-close" @click="onSnackbarChanged" />-->
    <!--      </template>-->
    <!--    </v-snackbar>-->

    <v-footer app elevation="3" class="bg-primary">
      <span class="mr-5">2023 &copy;</span>
    </v-footer>
  </v-layout>
  <teleport to="head">
    <meta name="theme-color" :content="theme.computedThemes.value[isDark].colors.primary" />
    <link rel="icon" :href="logo" type="image/svg+xml" />
  </teleport>

  <!-- login modal -->
  <v-dialog
    persistent
    class="dialog-modal-small"
    v-model="showLoginFormDialog"
    scrollable
    @keydown.esc="closeLoginFormDialog"
  >
    <v-card class="ma-0 pa-0 dialog-modal-small" :loading="loading">
      <v-card-title class="text-white bg-primary d-flex justify-space-between align-center">
        <span class="card-title-content">Account Login</span>
        <v-spacer />
        <v-icon class="text-white" icon="mdi-close" size="small" @click="closeLoginFormDialog" />
      </v-card-title>
      <v-card-text>
        <LoginForm v-bind:isModal="true" @close="closeLoginFormDialog" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
@use 'vuetify/_settings';
//
//html {
//  // Fix always scrollbar shown.
//  overflow-y: auto;
//  // Modern scrollbar style
//  scrollbar-width: thin;
//  scrollbar-color: map-get(settings.$grey, 'lighten-2') map-get(settings.$grey, 'base');
//}
//
//::-webkit-scrollbar {
//  width: 0.5rem;
//  height: 0.5rem;
//}
//
//::-webkit-scrollbar-track {
//  box-shadow: inset 0 0 0.5rem rgba(0, 0, 0, 0.1);
//  background-color: map-get(settings.$grey, 'lighten-2');
//}
//
//::-webkit-scrollbar-thumb {
//  border-radius: 0.5rem;
//  background-color: map-get(settings.$grey, 'base');
//  box-shadow: inset 0 0 0.5rem rgba(0, 0, 0, 0.1);
//}
//
//// Fixed a bug that the theme color is interrupted when scrolling
//.v-application {
//  overflow-y: auto;
//}
//
//// Fix app-bar's progress-bar
//.v-app-bar .v-progress-linear {
//  position: absolute;
//  bottom: 0;
//}
</style>
